import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd'
import Button from '@mui/material/Button'
import Icon from '@mui/material/Icon'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React from 'react'

import {
  ContentFlowDatePicker,
  ContentFlowMultiSelect,
  ContentFlowPage,
  ContentFlowSingleSelect,
  ContentFlowText,
} from '@/app/module/campaigns/types/common'

import { useWaFlowFieldError } from './hooks/use-wa-flow-field-error'
import WaFlowEditorElement from './wa-flow-editor-element'
import {
  appendElementToPage,
  PAGE_TITLE_MAX_LENGTH,
  reorderPageElements,
  updateElementDatePickerField,
  updateElementHeading,
  updateElementSelectField,
  updateElementTextField,
} from './wa-flow-editor-utils'

const EMPTY_PAGE_HELPER_TEXT = 'Add at least one content element to this page.'

type Props = {
  currentPage?: ContentFlowPage
  headingLabel: string
  pageMissingHelperText: string
  shouldFocusTitle?: boolean
  showErrorsOnMount?: boolean
  title: string

  onChange: (page: ContentFlowPage) => void
  onPageTitleFocus?: () => void
}

const WaFlowEditorElements: React.FC<Props> = ({
  currentPage,
  headingLabel,
  pageMissingHelperText,
  shouldFocusTitle,
  showErrorsOnMount = true,
  title,
  onChange,
  onPageTitleFocus,
}) => {
  const [addElementAnchorEl, setAddElementAnchorEl] = React.useState<HTMLElement | null>(null)
  const [newElementIds, setNewElementIds] = React.useState<string[]>([])

  const handleAddElementMenuClose = React.useCallback(() => {
    setAddElementAnchorEl(null)
  }, [])

  const handleAddElementMenuOpen = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAddElementAnchorEl(event.currentTarget)
  }, [])

  const handleAddElement = React.useCallback(
    (elementType: string) => {
      if (!currentPage) {
        return
      }

      const updatedPage = appendElementToPage(currentPage, elementType)
      const latestElement = updatedPage.elements[updatedPage.elements.length - 1]

      if (latestElement?.questionId) {
        setNewElementIds((value) => [...value, latestElement.questionId])
      }
      onChange(updatedPage)
      handleAddElementMenuClose()
    },
    [currentPage, handleAddElementMenuClose, onChange],
  )

  const handleElementFocus = React.useCallback((elementId: string) => {
    setNewElementIds((value) => value.filter((id) => id !== elementId))
  }, [])

  const handleDragEnd = React.useCallback(
    ({ destination, source }: DropResult) => {
      if (!currentPage || !destination || destination.index === source.index) {
        return
      }

      const updatedPage = reorderPageElements(currentPage, source.index, destination.index)
      onChange(updatedPage)
    },
    [currentPage, onChange],
  )

  const handleHeadingChange = React.useCallback(
    (elementId: string, key: 'heading' | 'subheading', value: string) => {
      if (!currentPage) {
        return
      }

      const updatedPage = updateElementHeading(currentPage, elementId, key, value)
      onChange(updatedPage)
    },
    [currentPage, onChange],
  )

  const handleTextChange = React.useCallback(
    (elementId: string, key: 'shortText' | 'longText', value: ContentFlowText) => {
      if (!currentPage) {
        return
      }

      const updatedPage = updateElementTextField(currentPage, elementId, key, value)
      onChange(updatedPage)
    },
    [currentPage, onChange],
  )

  const handleDatePickerChange = React.useCallback(
    (elementId: string, value: ContentFlowDatePicker) => {
      if (!currentPage) {
        return
      }

      const updatedPage = updateElementDatePickerField(currentPage, elementId, value)
      onChange(updatedPage)
    },
    [currentPage, onChange],
  )

  const handleSelectChange = React.useCallback(
    (
      elementId: string,
      key: 'singleSelect' | 'multiSelect',
      value: ContentFlowSingleSelect | ContentFlowMultiSelect,
    ) => {
      if (!currentPage) {
        return
      }

      const updatedPage = updateElementSelectField(currentPage, elementId, key, value)
      onChange(updatedPage)
    },
    [currentPage, onChange],
  )

  const handleElementRemove = React.useCallback(
    (elementId: string) => {
      if (!currentPage) {
        return
      }

      const elementIndex = currentPage.elements.findIndex((element) => element.questionId === elementId)

      if (elementIndex === -1) {
        return
      }

      const elements = currentPage.elements.filter((element) => element.questionId !== elementId)

      onChange({
        ...currentPage,
        elements,
      })
    },
    [currentPage, onChange],
  )

  const handlePageTitleChange = React.useCallback(
    (value: string) => {
      if (!currentPage) {
        return
      }

      onChange({
        ...currentPage,
        title: value,
      })
    },
    [currentPage, onChange],
  )

  return (
    <>
      <Typography variant="h6">{title}</Typography>

      {currentPage && (
        <Stack spacing={1} py={1}>
          <PageTitleField
            autoFocus={shouldFocusTitle}
            key={currentPage.id}
            page={currentPage}
            showErrorsOnMount={showErrorsOnMount && !shouldFocusTitle}
            onChange={handlePageTitleChange}
            onFocus={onPageTitleFocus}
          />
        </Stack>
      )}

      {currentPage ? (
        <>
          {!currentPage.elements.length && <Typography color="error.main">{EMPTY_PAGE_HELPER_TEXT}</Typography>}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`wa-flow-elements-${currentPage.id}`}>
              {(provided) => (
                <Stack ref={provided.innerRef} spacing={1.5} {...provided.droppableProps}>
                  {currentPage.elements.map((element, index) => {
                    const elementId = element.questionId

                    return (
                      <WaFlowEditorElement
                        element={element}
                        headingLabel={headingLabel}
                        index={index}
                        isNewElement={newElementIds.includes(elementId)}
                        key={elementId}
                        pageId={currentPage.id}
                        showErrorsOnMount={showErrorsOnMount && !newElementIds.includes(elementId)}
                        onDatePickerChange={handleDatePickerChange}
                        onElementRemove={handleElementRemove}
                        onFocus={handleElementFocus}
                        onHeadingChange={handleHeadingChange}
                        onSelectChange={handleSelectChange}
                        onTextChange={handleTextChange}
                      />
                    )
                  })}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        </>
      ) : (
        <Typography color="textSecondary">{pageMissingHelperText}</Typography>
      )}

      <Stack alignItems="flex-end" spacing={1}>
        <Button
          color="primary"
          disabled={!currentPage}
          endIcon={<Icon>arrow_drop_down</Icon>}
          id="wa-flow-add-element-button"
          onClick={handleAddElementMenuOpen}
          startIcon={<Icon>add</Icon>}
          variant="contained"
        >
          Add element
        </Button>
        <Menu
          anchorEl={addElementAnchorEl}
          disableRestoreFocus
          id="wa-flow-add-element-menu"
          MenuListProps={{
            'aria-labelledby': 'wa-flow-add-element-button',
          }}
          onClose={handleAddElementMenuClose}
          open={Boolean(addElementAnchorEl)}
        >
          <MenuItem
            onClick={() => {
              handleAddElement('heading')
            }}
          >
            Heading
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddElement('subheading')
            }}
          >
            Subheading
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddElement('singleSelect')
            }}
          >
            Single select
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddElement('multiSelect')
            }}
          >
            Multi select
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddElement('datePicker')
            }}
          >
            Date picker
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddElement('shortText')
            }}
          >
            Open Response (short)
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAddElement('longText')
            }}
          >
            Open Response (long)
          </MenuItem>
        </Menu>
      </Stack>
    </>
  )
}

type PageTitleFieldProps = {
  autoFocus?: boolean
  page: ContentFlowPage
  showErrorsOnMount: boolean

  onChange: (value: string) => void
  onFocus?: () => void
}

const PageTitleField: React.FC<PageTitleFieldProps> = ({ autoFocus, page, showErrorsOnMount, onChange, onFocus }) => {
  const [error, revealError] = useWaFlowFieldError(page.title || '', PAGE_TITLE_MAX_LENGTH, showErrorsOnMount)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!autoFocus) {
      return
    }

    inputRef.current?.focus()
    onFocus?.()
  }, [autoFocus, onFocus])

  return (
    <TextField
      fullWidth
      data-testid="wa-flow-page-title"
      error={!!error}
      helperText={error || undefined}
      inputRef={inputRef}
      label="Page title"
      required
      value={page.title || ''}
      variant="standard"
      onBlur={revealError}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

export default WaFlowEditorElements

export type { Props as WaFlowEditorElementsProps }
